/**
 * Story 049 — `recent-activity` widget body.
 *
 * Reads `GET /api/lyse/v1/audit?pageSize=10` per story §"Widget set".
 * The audit endpoint is *proposed* (it does not have a backend
 * requirement yet — see story §"Open questions").
 *
 * ANA-R2-02 (OQ-A2-2 a) — until that endpoint ships, the request is
 * GATED behind `AUDIT_FEED_ENABLED` so it never fires (it would 404 on
 * every dashboard load and dirty the console). The widget renders the
 * "Coming soon" placeholder directly while the feature is unshipped,
 * rather than reaching it via the is404 branch. When the audit-feed
 * endpoint lands, flip `AUDIT_FEED_ENABLED` to `true` (a later story
 * wires it) and the live list/skeleton/error paths below take over.
 *
 * UX brief §3.5 — compact list with verb-icon + actor + time.
 */
import { useMemo, type ReactElement } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { isAxiosError } from "axios";
import { WidgetRetryPill } from "./WidgetRetryPill.js";

/**
 * ANA-R2-02 — the tenant-scoped audit-feed endpoint (`GET /api/lyse/v1/audit`)
 * is not implemented yet. While `false`, the widget shows the "Coming soon"
 * placeholder and issues NO request. Flip to `true` once the endpoint ships.
 */
const AUDIT_FEED_ENABLED = false;

interface ActivityItem {
  id: string;
  at?: string | null;
  actor?: { displayName?: string } | null;
  action?: string | null;
  target?: { displayName?: string } | null;
  detail?: Record<string, unknown> | null;
}

interface ActivityResponse {
  items?: ActivityItem[];
  total?: number;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  const days = Math.round(hours / 24);
  return `${String(days)} d ago`;
}

function describe(item: ActivityItem): string {
  const actor = item.actor?.displayName ?? "Someone";
  const verb = item.action ?? "did something";
  const target = item.target?.displayName ?? "";
  return target ? `${actor} ${verb} ${target}` : `${actor} ${verb}`;
}

export function RecentActivity(): ReactElement {
  const api = useApi();
  const query = useQuery<ActivityResponse, unknown>({
    queryKey: ["dashboard", "recent-activity"],
    queryFn: async () => {
      const response = await api.get<ActivityResponse>("/audit", {
        params: { pageSize: 10 },
      });
      return response.data;
    },
    // ANA-R2-02 (OQ-A2-2 a) — gate the request: the audit endpoint does
    // not exist yet, so firing it guarantees a 404 on every dashboard
    // load. `enabled: false` keeps the query inert until the feature
    // ships, and we render the placeholder directly below.
    enabled: AUDIT_FEED_ENABLED,
    staleTime: 30_000,
    retry: false,
  });

  // Detect 404 → "endpoint not implemented yet" → soft empty state.
  const is404 = useMemo(() => {
    const err = query.error;
    if (!err) return false;
    if (isAxiosError(err)) return err.response?.status === 404;
    // The api-client unwraps AxiosError into AeroApiError; status is
    // forwarded as a number. Read defensively.
    if (
      typeof err === "object" &&
      "status" in err &&
      (err as { status?: number }).status === 404
    ) {
      return true;
    }
    return false;
  }, [query.error]);

  const items = useMemo(() => query.data?.items ?? [], [query.data?.items]);

  // ANA-R2-02 (OQ-A2-2 a) — while the audit-feed feature is unshipped,
  // short-circuit to the "Coming soon" placeholder. The query above is
  // `enabled: false`, so nothing was ever fetched. (Kept after the hooks
  // so the hook order stays stable for when the flag flips on.)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!AUDIT_FEED_ENABLED) {
    return <ComingSoonPlaceholder />;
  }

  if (query.isPending) {
    return (
      <Stack spacing={0.5} sx={{ px: 1, py: 1 }}>
        {Array.from({ length: 10 }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="text"
            width={`${String(60 + ((idx * 7) % 30))}%`}
          />
        ))}
      </Stack>
    );
  }

  if (is404) {
    return <ComingSoonPlaceholder />;
  }

  if (query.isError) {
    return (
      <WidgetRetryPill
        message="Couldn't load Recent activity."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: 3,
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          No activity yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Recent changes by you and your team appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem key={item.id} sx={{ py: 0.5, px: 1.5 }}>
          <ListItemText
            primary={describe(item)}
            secondary={relativeTime(item.at)}
            slotProps={{
              primary: { variant: "caption", color: "text.primary" },
              secondary: { variant: "caption", color: "text.secondary" },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function ComingSoonPlaceholder(): ReactElement {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
        py: 3,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Coming soon — activity feed will light up once the audit endpoint ships.
      </Typography>
    </Box>
  );
}
